import { Users, UserPlus, Clock, CheckCircle, XCircle, Ban, AlertCircle } from "lucide-react";
import { userStatus } from "@/utils/szStatus";

// Toolbar status-filter pills (key matches backend `status` query param).
export const statusFilters = [
  { key: "all", label: "All", icon: Users, countField: "total_users" },
  { key: "new", label: "New", icon: UserPlus, countField: "new_users" },
  { key: "pending", label: "Pending", icon: Clock, countField: "pending_users" },
  { key: "approved", label: "Approved", icon: CheckCircle, countField: "approved_users" },
  { key: "rejected", label: "Rejected", icon: XCircle, countField: "rejected_users" },
  { key: "suspended", label: "Suspended", icon: Ban, countField: "suspended_users" },
  { key: "bounced", label: "Bounced", icon: AlertCircle, countField: "bounced_users" },
];

// Row ⋯ status-change options (preserves status + notify flags used by the API).
export const approvalOptions = [
  { key: "new", label: "New", icon: UserPlus, iconColor: "text-blue-600", status: "new", notify: true },
  { key: "pending", label: "Pending", icon: Clock, iconColor: "text-amber-600", status: "pending", notify: true },
  { key: "approved_notify", label: "Approve with Notification", icon: CheckCircle, iconColor: "text-green-600", status: "approved", notify: true },
  { key: "approved_silent", label: "Approve without Notification", icon: CheckCircle, iconColor: "text-green-400", status: "approved", notify: false },
  { key: "rejected", label: "Rejected", icon: XCircle, iconColor: "text-red-600", status: "rejected", notify: true },
  { key: "suspended", label: "Suspended", icon: Ban, iconColor: "text-gray-600", status: "suspended", notify: true },
];

// Resolve a user's approval status to its SZ badge config {label,fg,bg}.
export const getApprovalConfig = (approvalStatus) => userStatus(approvalStatus || "new");

// Deterministic avatar colour from a string (id / name) — SZ brand palette.
const AVATAR_COLORS = ["#572B7C", "#78287C", "#28C0D0", "#0F766E", "#2563EB", "#15803D", "#D97706", "#0E7490"];
export const avatarColor = (seed = "") => {
  let h = 0;
  const s = String(seed);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// Most users only ever store full_name — first_name/last_name are populated by
// some signup paths and not others — so anything showing a user's name has to
// fall back to it, or real accounts render as a blank row.
export const userDisplayName = (user) => {
  const full = (user.full_name || "").trim();
  if (full) return full;
  const parts = `${(user.first_name || "").trim()} ${(user.last_name || "").trim()}`.trim();
  return parts || "";
};

export const userInitials = (user) => {
  const name = userDisplayName(user);
  if (name) {
    const [f = "", l = ""] = name.split(/\s+/);
    const initials = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
    if (initials) return initials;
  }
  return (user.email || "U").charAt(0).toUpperCase();
};

export const getPageNumbers = (currentPage, totalPages, maxVisible = 5) => {
  if (totalPages <= maxVisible) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const half = Math.floor(maxVisible / 2);
  let start = currentPage - half;
  let end = currentPage + half;
  if (start < 1) { start = 1; end = maxVisible; }
  if (end > totalPages) { end = totalPages; start = totalPages - maxVisible + 1; }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};
